import React, { useState, useCallback, useEffect } from 'react';
import { FIREBASE_AUTH, FIREBASE_DB } from '../firebase.config';
import { collection, query, getDocs, doc, getDoc, updateDoc } from 'firebase/firestore';
import { RefreshCw, CheckCircle, XCircle } from 'lucide-react';

const AdminReferrals = () => {
  const [referrals, setReferrals] = useState([]);
  const [loading, setLoading] = useState(true);

  const fetchReferrals = useCallback(async () => {
    try {
      setLoading(true);
      const referralsQuery = query(collection(FIREBASE_DB, 'referrals'));
      const querySnapshot = await getDocs(referralsQuery);
      const referralsList = [];
      
      for (const docSnapshot of querySnapshot.docs) {
        const referralData = docSnapshot.data();
        // Get employee details
        const employeeDocRef = doc(FIREBASE_DB, 'users', referralData.employeeId);
        const employeeDocSnapshot = await getDoc(employeeDocRef);
        const employeeData = employeeDocSnapshot.exists() ? employeeDocSnapshot.data() : { name: 'Unknown Employee' };
        
        referralsList.push({
          id: docSnapshot.id,
          ...referralData,
          employeeName: employeeData.name || 'Unknown Employee'
        });
      }
      
      setReferrals(referralsList.sort((a, b) => new Date(b.submissionDate) - new Date(a.submissionDate)));
    } catch (error) {
      console.error('Error fetching referrals:', error);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchReferrals();
  }, [fetchReferrals]);

  const updateReferralStatus = async (referralId, newStatus) => {
    try {
      const docRef = doc(FIREBASE_DB, 'referrals', referralId);
      await updateDoc(docRef, {
        status: newStatus,
        reviewedAt: new Date().toISOString(),
        reviewedBy: FIREBASE_AUTH.currentUser.uid
      });
      await fetchReferrals();
    } catch (error) {
      console.error('Error updating referral status:', error);
    }
  };

  return (
    <div className="min-h-screen bg-gray-100 py-8">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="bg-white rounded-lg shadow-md p-6">
          <div className="flex items-center justify-between mb-6">
            <h1 className="text-2xl font-semibold text-gray-900">
              Employee Referrals
            </h1>
            <button
              onClick={fetchReferrals}
              className="flex items-center px-3 py-2 bg-accent text-white rounded-md hover:bg-accent-dark transition-colors"
            >
              <RefreshCw className="w-4 h-4 mr-2" />
              Refresh
            </button>
          </div>

          {loading ? (
            <div className="flex justify-center items-center py-12">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-accent"></div>
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="min-w-full divide-y divide-gray-200">
                <thead className="bg-gray-50">
                  <tr>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Employee
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Candidate
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Position
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Contact Info
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Submission Date
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Status
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Actions
                    </th>
                  </tr>
                </thead>
                <tbody className="bg-white divide-y divide-gray-200">
                  {referrals.map((referral) => (
                    <tr key={referral.id} className="hover:bg-gray-50">
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="text-sm font-medium text-gray-900">
                          {referral.employeeName}
                        </div>
                        <div className="text-sm text-gray-500">
                          {referral.employeeEmail}
                        </div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="text-sm font-medium text-gray-900">
                          {referral.candidateName}
                        </div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="text-sm text-gray-900">
                          {referral.positionReferredFor}
                        </div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="text-sm text-gray-500">
                          <div>Email: {referral.candidateEmail}</div>
                          <div>Phone: {referral.candidatePhone}</div>
                        </div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="text-sm text-gray-500">
                          {new Date(referral.submissionDate).toLocaleDateString()}
                        </div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <span className={`px-2 py-1 inline-flex text-xs leading-5 font-semibold rounded-full 
                          ${(referral.status || 'pending') === 'approved' ? 'bg-green-100 text-green-800' : 
                            (referral.status || 'pending') === 'rejected' ? 'bg-red-100 text-red-800' : 
                            'bg-yellow-100 text-yellow-800'}`}>
                          {((referral.status || 'pending').charAt(0).toUpperCase() + (referral.status || 'pending').slice(1))}
                        </span>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm font-medium">
                        <div className="flex items-center space-x-3">
                          {(!referral.status || referral.status === 'pending') && (
                            <>
                              <button
                                onClick={() => updateReferralStatus(referral.id, 'approved')}
                                className="text-green-600 hover:text-green-900"
                                title="Approve Referral"
                              >
                                <CheckCircle className="w-5 h-5" />
                              </button>
                              <button
                                onClick={() => updateReferralStatus(referral.id, 'rejected')}
                                className="text-red-600 hover:text-red-900"
                                title="Reject Referral"
                              >
                                <XCircle className="w-5 h-5" />
                              </button>
                            </>
                          )}
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
              {referrals.length === 0 && (
                <div className="text-center py-12 text-gray-500">
                  No referrals submitted yet
                </div>
              )}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default AdminReferrals; 